import React from 'react';
import { useState, useEffect } from 'react';
import CustomRadioButton from '../form/CustomRadioButton';
import { Field, Form, withTypes } from 'react-final-form';
import { useNavigate } from 'react-router-dom';
import { Table } from 'reactstrap';
import { useDict } from '../../contexts/LanguageContext';
import ROUTE_NAME from '../../routes/RouteName';
import { appendLocale } from '../../utils/helper';
import Modal from './Modal';
import { getPreInvestmentScreeningQuestions, postSubmitPreInvestmentScreening } from './src/preInvestmentScreeningApis';
import { toast } from 'react-hot-toast';
import ReactHtmlParser from 'react-html-parser';
import enErrors from '../../languages/enErrors.json';
import thErrors from '../../languages/thErrors.json';
import { usePreInvestmentScreeningModal } from '../../contexts/LanguageContext';

const PreInvestmentScreeningModal = ({ isOpen, closeModal, isAuthenticated, isSubmittedWealthDeclaration }) => {
    const dict = useDict();
    const navigate = useNavigate();

    const { togglePreInvestmentScreeningModal } = usePreInvestmentScreeningModal();

    const [questions, setQuestions] = useState([]);
    const [preScreeningIds, setPreScreeningIds] = useState([]);
    const [errorIds, setErrorIds] = useState([]);
    const [isFetching, setIsFetching] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);

    useEffect(() => {
        if (isAuthenticated) {
            setIsFetching(true);
            togglePreInvestmentScreeningModal();
            getPreInvestmentScreeningQuestions(handleGetPreInvestmentScreeningQuestions);
            setIsFetching(false);
        }
    }, [isAuthenticated]);

    const handleGetPreInvestmentScreeningQuestions = (response) => {
        setQuestions(response.data.data.pre_screening);

        const preScreeningIds = response.data.data.pre_screening.map(item => item.options[0].pre_screening_id);
        setPreScreeningIds(preScreeningIds);
    };

    const handleClose = () => {
        closeModal();
        sessionStorage.clear();
        navigate(ROUTE_NAME.HOME);
    };

    const handleSubmit = (values) => {
        const newErrorIds = [];

        preScreeningIds.forEach((id) => {
            if (!values['question_' + id]) {
                newErrorIds.push(id);
            }
        });

        setErrorIds(newErrorIds);

        if (newErrorIds.length === 0) {
            setIsLoading(true);

            const formattedValues = Object.entries(values).map(([key, value]) => {
                const questionId = parseInt(key.split('_')[1], 10);
                return {
                    question_id: questionId,
                    option_id: value
                };
            });

            const requestData = {
                'data': formattedValues
            };

            postSubmitPreInvestmentScreening(
                requestData,
                handlePostSubmitPreInvestmentScreening,
                handlePostSubmitPreInvestmentScreeningError
            );

            setIsLoading(false);
        }
    };

    const handlePostSubmitPreInvestmentScreening = (response) => {
        closeModal();
        if (isSubmittedWealthDeclaration) {
            navigate(ROUTE_NAME.INVEST_NOW);
        } else {
            navigate(ROUTE_NAME.FIRST_TIME_DASHBOARD);
        }
    };

    const handlePostSubmitPreInvestmentScreeningError = (error) => {
        setIsSubmitDisabled(true);

        const language = localStorage.getItem('language') === 'en' ? 'en' : 'th';

        if (error.response.data.error === 'pre_screening_failure') {
            toast.loading(language === 'en' ? enErrors.pre_screening_failure : thErrors.pre_screening_failure, {
                duration: 5000,
                icon: '❌',
                toastId: 'loadingToast',
            });

            setTimeout(() => {
                toast.dismiss('loadingToast');
                handleClose();
            }, 5000);
        } else if (error.response.data.error === 'login_after_pre_screening_failed') {
            toast.error(language === 'en' ? enErrors.login_after_pre_screening_failed : thErrors.login_after_pre_screening_failed, {
                duration: 5000,
            });
        } else {
            toast.error(language === 'en' ? enErrors.general_error : thErrors.general_error, {
                duration: 5000,
            });
        }
    };

    const renderQuestions = (questions) => {
        if (isFetching) {
            return (
                <tbody>
                    <tr>
                        <td>{dict.fetchingData}</td>
                    </tr>
                </tbody>
            );
        } else {
            return (
                <tbody>
                    {questions && questions.length > 0 ? (
                        questions.map((data, index) => (
                            <tr key={index} className="d-flex align-items-center">
                                <td>
                                    <div className="align-text-left pre-investment-modal-question-div">
                                        {ReactHtmlParser(data[appendLocale('question')])}
                                    </div>

                                    <div className="form__form-group-field d-flex flex-column align-items-start">
                                        <div className="form__form-group-input-wrap d-flex pl-2 pb-1 pre-investment-screening-radio-div">
                                            {data.options.map((option) => (
                                                <Field
                                                    name={'question_' + option.pre_screening_id.toString()}
                                                    type="radio"
                                                    value={option.id}
                                                >
                                                    {({ input, meta }) => (
                                                        <CustomRadioButton
                                                            input={input}
                                                            meta={meta}
                                                            label={option[appendLocale('option')]}
                                                            className="colored"
                                                            divClassName="width-fit-content"
                                                            labelClassName="pl-2"
                                                            topLabelClassName="white-space-nowrap"
                                                        />
                                                    )}
                                                </Field>
                                            ))}

                                        </div>

                                        {
                                            errorIds.includes(data.options[0].pre_screening_id) && (
                                                <span className="validation-text">* {dict.selectOption}</span>
                                            )
                                        }
                                    </div>
                                </td>
                            </tr>
                        ))
                    ) : null}
                </tbody>
            );
        }
    };

    const { Form } = withTypes();

    return (
        <Modal
            toggle={handleClose}
            isOpen={isOpen}
            onCancel={handleClose}
            cancelText={dict.logoutNav}
            submitText={dict.submit}
            submitShown
            submitBtnProps={{
                form: 'preInvestmentScreeningForm',
                disabled: isLoading || isSubmitDisabled,
                className: 'btn rounded mb-0 width-180',
            }}
            headerType="invest"
            title={dict.preInvestmentScreening}
            backdrop="static"
            size="md"
        >
            <div className="modal__body pb-2">
                <Form
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                >
                    {({ dirty, handleSubmit, errors }) => (
                        <form id="preInvestmentScreeningForm" onSubmit={handleSubmit}>
                            <Table responsive className="table--bordered">
                                {renderQuestions(questions)}
                            </Table>
                        </form>
                    )}
                </Form>
            </div>
        </Modal>
    );
};

export default PreInvestmentScreeningModal;
